import { EControlType } from "components/ControlWrapper";
import { friendlyValueToFlexibleFieldValue } from "global/Application";
import { IFlexibleField, IFlexibleFieldValue, IGroupConfigurationConfig, ITierLevel, TValue } from "model/FlexibleAttributeModel";
import { IFlexibleFormDefinition } from "model/FlexibleFormModel";
import { v4 as uuidv4 } from 'uuid';

export class FlexibleFieldGenerator {
    private readonly release: IGroupConfigurationConfig;

    constructor(release: IGroupConfigurationConfig) {
        this.release = release;
    }

    private generateFieldsForFlexibleFormDefinition(flexibleFormDefinition: IFlexibleFormDefinition, tiers?: ITierLevel[] | null): IFlexibleField[] {
        let fields: IFlexibleField[] = [];

        for (let tab of flexibleFormDefinition.tabs) {
            for (let section of tab.sections) {
                for (let row of section.rows) {
                    for (let attr of row.attributes) {
                        let values: IFlexibleFieldValue[] = [];

                        // NOTE: we can only have PCP/Specialist splits when it's tier enabled
                        let generateTiers = attr.attribute.bcbs_istierenabled && tiers && tiers.length > 0;
                        let generatePcpSpecialist = attr.attribute.bcbs_isseparatepcpandspecialist;

                        // ignore
                        let defaultValue = null as TValue | null;
                        if (attr.attribute.bcbs_defaultvalue)
                            if (attr.masterAttribute.bcbs_attributetypecode !== EControlType.message)
                                defaultValue = friendlyValueToFlexibleFieldValue(
                                    this.release,
                                    attr.masterAttribute.bcbs_attributetypecode as EControlType,
                                    attr.attribute.bcbs_defaultvalue,
                                    attr.attribute,
                                    attr.masterAttribute,
                                    null,
                                    null
                                );

                        if (!generateTiers) {
                            values.push({
                                id: uuidv4(),
                                tierRef: null,
                                type: 'primary',
                                value: defaultValue
                            });
                        } else {
                            tiers?.forEach(tier => {

                                if (attr.attribute.bcbs_isexcludeoutofnetwork)
                                    if (tier.ion)
                                        return;

                                if (tier.pcp && generatePcpSpecialist ) {
                                    values.push({
                                        id: uuidv4(),
                                        tierRef: {
                                            id: tier.id,
                                            name: tier.name
                                        },
                                        type: 'pcp',
                                        value: defaultValue
                                    });
                                    values.push({
                                        id: uuidv4(),
                                        tierRef: {
                                            id: tier.id,
                                            name: tier.name
                                        },
                                        type: 'specialist',
                                        value: defaultValue
                                    });
                                } else {
                                    values.push({
                                        id: uuidv4(),
                                        tierRef: {
                                            id: tier.id,
                                            name: tier.name
                                        },
                                        type: 'primary',
                                        value: defaultValue
                                    });
                                }
                            });
                        }

                        fields.push({
                            configRef: {
                                id: attr.attribute.bcbs_config_attributeid!,
                                name: attr.attribute.bcbs_name!
                            },
                            id: uuidv4(),
                            masterAttributeID: attr.attribute._bcbs_config_masterattributeid_value!,
                            name: attr.attribute.bcbs_label || attr.attribute.bcbs_name || attr.masterAttribute.bcbs_name || "",
                            values: values
                        });
                    }
                }
            }
        }

        return fields;
    }

    generateFieldsForCategoryId(categoryId: string, tiers?: ITierLevel[] | null): IFlexibleField[] {
        let flexibleFormDefinition = this.release.cache.categoryFormDefinitions[categoryId];
        if (!flexibleFormDefinition)
            return [];

        return this.generateFieldsForFlexibleFormDefinition(flexibleFormDefinition, tiers);
    }

    generateAttributeSetFields(attributeSetId: string): IFlexibleField[] {
        let flexibleFormDefinition = this.release.cache.attributeSetFormDefinitions[attributeSetId];
        if (!flexibleFormDefinition)
            return [];

        return this.generateFieldsForFlexibleFormDefinition(flexibleFormDefinition);
    }

    generateNonStandardFields(sectionId: string, tiers?: ITierLevel[]): IFlexibleField[] {
        let flexibleFormDefinition = this.release.cache.nonStandardFormDefinitions[sectionId];
        if (!flexibleFormDefinition)
            return [];

        return this.generateFieldsForFlexibleFormDefinition(flexibleFormDefinition, tiers);
    }

    generateNonStandardTemplateFields(sectionId: string): IFlexibleField[] {
        let flexibleFormDefinition = this.release.cache.nonStandardFormDefinitions[sectionId];
        if (!flexibleFormDefinition)
            return [];

        return this.generateFieldsForFlexibleFormDefinition(flexibleFormDefinition);
    }

    generateGroupConfigurationFields(): IFlexibleField[] {
        return this.generateFieldsForCategoryId('4f432ecf-bc5d-ec11-8f8f-000d3a342d18');
    }

    generateBenefitFields(categoryId: string, tiers?: ITierLevel[] | null): IFlexibleField[] {
        return this.generateFieldsForCategoryId(categoryId, tiers);
    }

    generatePlanFields(): IFlexibleField[] {
        return this.generateFieldsForCategoryId('d33bde62-d55d-ec11-8f8f-000d3a342d18');
    }

    generateStopLossFields(): IFlexibleField[] {
        return this.generateFieldsForCategoryId('c1816640-d778-ec11-8d21-0022480b0e40');
    }

    generateFeesAndPremiumsFields(): IFlexibleField[] {
        return this.generateFieldsForCategoryId('7c8c262c-d778-ec11-8d21-0022480b0e40');
    }

    generateAccountsReceivablesFields(): IFlexibleField[] {
        return this.generateFieldsForCategoryId('c4d3ccf4-b08d-ec11-b400-0022480b6b1d');
    }

    generateGroupNumberFields(): IFlexibleField[] {
        return this.generateFieldsForCategoryId('23153cda-d08d-ec11-b400-0022480b6b1d');
    }
}